import React from 'react';
import IpsFileAnalysisPanel from './IpsFileAnalysisPanel';

/**
 * IpsScanFileRow
 *
 * Renders a single matched IPS file plus its Bedrock field-extraction
 * analysis. Purely presentational - analysis is tracked and polled centrally
 * in IpsScanner.js (starting the moment the scan finds the file, regardless
 * of whether this row is expanded) and passed in as the `analysis` prop, so
 * collapsing/expanding this row never re-triggers or re-fetches anything.
 */
const IpsScanFileRow = ({
  file,
  analysis,
  isLatestForRename = false,
  suggestedName = '',
  draftName = '',
  isSelected = false,
  isRenaming = false,
  onDraftNameChange = null,
  onToggleSelect = null,
  onRenameFile = null,
  showRenameControls = true,
  isRenameTarget = false,
  canSetAsRenameTarget = false,
  onSetAsRenameTarget = null,
}) => {
  const renameStatus = file.renameStatus;
  const renamedName = file.renamedName;
  const renameError = file.renameError;
  const displayFileName = renamedName || file.name;

  return (
    <li className="mb-3">
      <div className="mb-1 d-flex align-items-center gap-2 flex-wrap">
        <span>
          {file.webUrl ? (
            <a href={file.webUrl} target="_blank" rel="noopener noreferrer">
              {displayFileName}
            </a>
          ) : (
            <span>{displayFileName}</span>
          )}
        </span>
        {renameStatus === 'renamed' && (
          <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">
            Renamed
          </span>
        )}
        {isRenameTarget && (
          <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle">
            Used for Rename + Excel Export
          </span>
        )}
        {!isRenameTarget && canSetAsRenameTarget && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm py-0"
            onClick={() => onSetAsRenameTarget?.()}
          >
            Use for Rename + Export
          </button>
        )}
      </div>
      {showRenameControls && isLatestForRename ? (
        <div className="mt-2 border rounded p-2 bg-light-subtle">
          <div className="small text-muted mb-1">Suggested filename</div>
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(event) => onToggleSelect?.(event.target.checked)}
                aria-label="Select file for bulk rename"
              />
            </span>
            <input
              type="text"
              className="form-control"
              value={draftName || suggestedName || ''}
              onChange={(event) => onDraftNameChange?.(event.target.value)}
              placeholder="Last-First-IPS-Signed-YYYY-MM-DD.pdf"
              disabled={isRenaming}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => onRenameFile?.()}
              disabled={isRenaming || !(draftName || suggestedName || '').trim()}
            >
              {isRenaming ? 'Renaming...' : 'Rename'}
            </button>
          </div>
          {renameStatus === 'renamed' && (
            <div className="small text-success mt-1">
              Already renamed to {renamedName || (draftName || suggestedName)}
            </div>
          )}
          {renameStatus === 'rename_error' && (
            <div className="small text-danger mt-1">
              Rename failed: {renameError || 'Unknown error'}
            </div>
          )}
        </div>
      ) : showRenameControls ? (
        <div className="small text-muted mt-1">Older match (ignored for rename)</div>
      ) : null}
      <IpsFileAnalysisPanel analysis={analysis} />
    </li>
  );
};

export default IpsScanFileRow;

